import { UnstyledButton } from '@mantine/core';
import { useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import type { SplitPaneHandle } from './SplitPane';
import classes from './SplitResizer.module.css';

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

export interface SplitResizerProps {
  /**
   * Доп. стили ресайзера (включая CSS-переменные). Внешний вид настраивается:
   * `--split-resizer-color` (цвет линии без ховера, по умолчанию прозрачная),
   * `--split-resizer-hover-color`, `--split-resizer-size`.
   * @example <Split.Resizer style={{ '--split-resizer-color': 'var(--mantine-color-gray-3)' }} />
   */
  style?: React.CSSProperties & Partial<Record<`--${string}`, string | number>>;
  __beforeRef?: React.RefObject<SplitPaneHandle | null>;
  __afterRef?: React.RefObject<SplitPaneHandle | null>;
  __onSaveLeftPercent?: (percent: number) => void;
}

/**
 * Разделитель между панелями (подход как в @gfazioli/mantine-split-pane).
 * Получает refs панелей и колбек сохранения через cloneElement из Split.
 */
export function SplitResizer({
  style,
  __beforeRef,
  __afterRef,
  __onSaveLeftPercent,
}: SplitResizerProps) {
  const [isResizing, setIsResizing] = useState(false);
  const resizerRef = useRef<HTMLButtonElement>(null);

  const applyVerticalSize = (beforeWidth: number, afterWidth: number) => {
    const before = __beforeRef?.current;
    const after = __afterRef?.current;
    if (!before?.splitPane || !after?.splitPane) return;
    before.splitPane.style.width = `${beforeWidth}px`;
    after.splitPane.style.width = `${afterWidth}px`;
  };

  const parseStyleLength = (v: string): number => {
    const t = v.trim();
    const n = parseFloat(t);
    if (Number.isNaN(n)) return 0;
    if (t.endsWith('rem'))
      return n * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16);
    return n;
  };

  const getResizerLayoutWidth = (resizerEl: HTMLElement): number => {
    const w = resizerEl.getBoundingClientRect().width;
    const s = getComputedStyle(resizerEl);
    return w + parseStyleLength(s.marginLeft) + parseStyleLength(s.marginRight);
  };

  const processVerticalSize = (deltaX: number) => {
    const before = __beforeRef?.current;
    const after = __afterRef?.current;
    if (!before?.splitPane || !after?.splitPane || !resizerRef.current) return;
    const beforePane = before.splitPane;
    const container = beforePane.parentElement;
    if (!container) return;
    const total = container.getBoundingClientRect().width;
    const resizerW = getResizerLayoutWidth(resizerRef.current);
    const totalAvailable = total - resizerW;
    if (totalAvailable <= 0) return;
    const currentBefore = beforePane.getBoundingClientRect().width;
    const minB = before.getMinWidth() ?? 0;
    const maxB = before.getMaxWidth() ?? totalAvailable;
    const minA = after.getMinWidth() ?? 0;
    const maxA = after.getMaxWidth() ?? totalAvailable;
    const low = Math.max(minB, totalAvailable - maxA);
    const high = Math.min(maxB, totalAvailable - minA);
    const beforeWidth = clamp(currentBefore + deltaX, low, high);
    const afterWidth = totalAvailable - beforeWidth;
    applyVerticalSize(beforeWidth, afterWidth);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!__beforeRef?.current?.splitPane || !__afterRef?.current?.splitPane) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const container = __beforeRef.current.splitPane.parentElement;
    if (!container) return;

    const onMove = (ev: PointerEvent) => {
      if (
        !__beforeRef?.current?.splitPane ||
        !__afterRef?.current?.splitPane ||
        !resizerRef.current
      )
        return;
      const resizerRect = resizerRef.current.getBoundingClientRect();
      const resizerWidth = parseFloat(getComputedStyle(resizerRef.current).width) || 0;
      const deltaX = ev.clientX - resizerRect.left - resizerWidth / 2;
      processVerticalSize(deltaX);
    };

    const onUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);

      const before = __beforeRef?.current?.splitPane;
      const after = __afterRef?.current?.splitPane;
      if (before && after && container && resizerRef.current && __onSaveLeftPercent) {
        const total = container.getBoundingClientRect().width;
        const totalAvailable = total - getResizerLayoutWidth(resizerRef.current);
        const beforeWidth = before.getBoundingClientRect().width;
        if (totalAvailable > 0) {
          __onSaveLeftPercent((beforeWidth / totalAvailable) * 100);
        }
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <UnstyledButton
      ref={resizerRef}
      type='button'
      aria-label='Resize'
      data-orientation='vertical'
      data-resizing={isResizing || undefined}
      className={classes.resizer}
      style={{ flexShrink: 0, ...style }}
      onPointerDown={onPointerDown}
    />
  );
}
